<template>
  <section
    id="visitor-info"
    class="visitor-info"
  >
    <header class="visitor-info__container">
      <c-headline class="mb-sm">
        <slot name="title" />
      </c-headline>

      <slot name="introduction" />
    </header>

    <div
      v-if="IS_IE"
      class="visitor-info__container"
      v-html="$t('ieMessage')"
    />

    <template v-else>
      <div class="visitor-info__container">
        <c-headline
          class="mb-sm"
          level="2"
        >
          {{ $t('titleInfo') }}
        </c-headline>

        <div class="visitor-info__info">
          <c-form-date
            v-if="maxDate"
            :value="selectedDate"
            color="primary"
            inline
            inline-label
            :max-date="maxDate"
            @change="updateSelectedDate"
          >
            {{ $t('labelDate') }}
          </c-form-date>

          <c-form-select
            id="ticket-number"
            v-model="ticketNumber"
            color="neutral"
            inline
            inline-label
          >
            {{ $t('labelNumber') }}
            <template #options>
              <option value="0">
                {{ $t('selectNumber') }}
              </option>
              <option
                v-for="n in maxTicketNumber"
                :id="'ticker-number-' + n"
                :key="n"
                :value="n"
              >
                {{ n }}
              </option>
              <option value="redirect">
                {{ $t('groupRegistration') }}
              </option>
            </template>
          </c-form-select>
        </div>
      </div>

      <div
        v-if="ticketTypesLoaded && !reservation && ticketNumber"
        class="visitor-info__container"
      >
        <c-headline
          class="mb-sm"
          level="2"
        >
          {{ $t('titleTickets') }}
        </c-headline>

        <slot name="introduction-tickets" />

        <div class="visitor-info__tickets">
          <c-form-select
            v-for="(item, index) in $store.state.ticketSelection.tickets"
            :id="'ticket-' + index"
            :key="index"
            color="neutral"
            :model-value="index + '-' + item"
            @update:modelValue="updateTicketType"
          >
            {{ index + 1 }}. <span class="sr-only">{{ $t('person') }}</span>
            <template #options>
              <option
                v-for="(typeName, typeID) in ticketTypes[$i18n.locale]"
                :key="typeID"
                :value="index + '-' + typeID"
              >
                {{ typeName }}
              </option>
            </template>
          </c-form-select>
        </div>
      </div>
    </template>

    <c-banner
      v-if="$slots.footer"
      color="primary"
      center
    >
      <slot name="footer" />
    </c-banner>
  </section>
</template>

<script>
import CBanner from './banner.vue'
import CFormDate from './formDate.vue'
import CFormSelect from './formSelect.vue'
import CHeadline from './headline.vue'

import api from '../api.js'
import dateHelper from '../functions/dateHelper.js'
import eventBus from '../eventBus.js'
import labelMixin from '../mixins/label.js'

import { IS_IE, GAP_ALL_DAY } from '../settings.js'

export default {
  name: 'CVisitorInfo',

  components: {
    CBanner,
    CFormDate,
    CFormSelect,
    CHeadline
  },

  mixins: [labelMixin],

  props: {
    reservation: {
      type: Boolean
    }
  },

  data () {
    return {
      ticketTypes: {},
      ticketTypesLoaded: false,
      tracked: false
    }
  },

  computed: {
    IS_IE: () => IS_IE,
    ticketNumber: {
      get () {
        if (this.reservation) {
          return this.$store.state.selectedReservationsAmount
        }

        return this.$store.state.selectedTicketsAmount
      },
      set (value) {
        this.updateTicketNumber(value)
      }
    },
    maxDate () {
      return this.$store.state.maxDate
    },
    selectedDate () {
      return this.$store.state.ticketSelection.selectedDate
    },
    maxTicketNumber () {
      return this.$store.state.ticketSelection.maxTicketNumber
    },
    isOnToursRoute () {
      return this.$route.name.startsWith('booking-tours') || this.$route.name.startsWith('yullbe')
    }
  },

  async created () {
    try {
      const response = await api.get('openings/last/')
      this.$store.commit('setMaxDate', response.data)
    } catch {
      // If no date is returned, make next 3 months available.
      const date = new Date()
      date.setDate(date.getDate() + 90)
      this.$store.commit('setMaxDate', dateHelper.formatDate(date))
    }

    // Pre-fill date from optional route parameter.

    const dateFromRoute = new Date(this.$route.params.date)

    if (!Number.isNaN(dateFromRoute.getTime())) {
      this.updateSelectedDate(dateHelper.formatDate(dateFromRoute))
    }

    // Pre-fill ticket number from optional route parameter.

    const ticketNumberFromRoute = Math.min(parseInt(this.$route.params.people), this.maxTicketNumber)

    if (!Number.isNaN(ticketNumberFromRoute)) {
      this.updateTicketNumber(ticketNumberFromRoute)
    }

    eventBus.on('articlesLoaded', () => this.loadTicketTypes())

    this.loadTicketTypes()
  },

  beforeDestroy () {
    eventBus.off('articlesLoaded')
  },

  methods: {
    loadTicketTypes () {
      this.ticketTypesLoaded = false

      if (Object.keys(this.$store.state.articles).length > 0) {
        const labels = {
          de: {},
          en: {}
        }

        for (const id in this.$store.state.articles) {
          const article = this.$store.state.articles[id]

          if (article.is_ticket === true && article.is_event === false) {
            labels.de[id] = article.text_de
            labels.en[id] = article.text_en
          }
        }

        this.ticketTypes = labels
        this.ticketTypesLoaded = true
      }
    },

    updateTicketNumber (value) {
      if (value === 'redirect') {
        window.location = this.$t('groupRedirect')
        return
      }

      if (this.reservation) {
        this.$store.commit('updateReservationAmount', value)

        if (this.$store.state.cart.reservations.gap > 0) {
          const gap = {
            gap: this.$store.state.cart.reservations.gap,
            date: this.$store.state.cart.reservations.date
          }

          this.$store.dispatch('addReservationToCart', { gap, amount: value })
        }

        return
      }

      this.$store.dispatch('updateTicketNumber', value)
      const ticketsFromCart = this.$store.state.cart.tickets

      if (ticketsFromCart.total) {
        const gap = {
          gap: ticketsFromCart.gap,
          date: ticketsFromCart.date
        }

        this.$store.dispatch('addTicketsToCart', { gap })

        return
      }

      if (this.isOnToursRoute) {
        this.updateAllDayTickets()
      }
    },

    updateTicketType (value) {
      value = value.split('-')

      this.$store.commit('ticketTypeSelected', [
        parseInt(value[0]),
        parseInt(value[1])
      ])

      // Update the cart
      if (this.$store.state.cart.tickets.total) {
        const gap = {
          gap: this.$store.state.cart.tickets.gap,
          date: this.$store.state.cart.tickets.date
        }

        this.$store.dispatch('addTicketsToCart', { gap })
      }
    },

    async updateSelectedDate (value) {
      if (!value) {
        value = dateHelper.formatDate(new Date())
        this.$store.commit('setSelectedDate', value)
      }

      this.$store.dispatch('calculateArrivalDates', { currentDate: value })

      if (this.$store.state.cart.tours.total) {
        await this.$store.dispatch('removeFromCart', { endpoint: 'tours' })
      }

      if (this.$store.state.cart.tickets.gap === GAP_ALL_DAY) {
        await this.updateAllDayTickets()
      }
    },

    updateAllDayTickets () {
      const gap = {
        gap: GAP_ALL_DAY,
        date: this.selectedDate
      }

      return this.$store.dispatch('addTicketsToCart', { gap })
    }
  }
}
</script>

<i18n locale="en">
{
  "titleInfo": "Visit info",
  "titleTickets": "Tickets",
  "labelDate": "Date",
  "labelNumber": "Number of tickets",
  "selectNumber": "Please select",
  "groupRegistration": "Register a group",
  "groupRedirect": "http://www.miniatur-wunderland.com/visit/group-offers/groupregistration/",
  "person": "person"
}
</i18n>

<i18n locale="de">
{
  "titleInfo": "Besuchsdaten",
  "titleTickets": "Eintrittskarten",
  "labelDate": "Datum",
  "labelNumber": "Anzahl Eintrittskarten",
  "selectNumber": "Bitte wählen",
  "groupRegistration": "Gruppenanmeldung",
  "groupRedirect": "http://www.miniatur-wunderland.de/besuch/gruppenangebote/gruppenanmeldung/",
  "person": "Person"
}
</i18n>

<style lang="scss">
@import "../css/variables";
@import "../css/mixins";

.visitor-info {
  @include vertical-spacing;

  background-color: #fff;
}

.visitor-info__container {
  padding: $gutter-sm;

  &:not(:first-child) {
    border-top: $border-dashed;
  }

  @media (min-width: $breakpoint-md) {
    padding: $gutter-md;
  }
}

.visitor-info__introduction {
  > :last-child {
    margin-bottom: 0;
  }
}

.visitor-info__heading {
  font-size: 1.25em;
  text-align: center;
}

.visitor-info__info {
  display: grid;
  justify-content: start;
  gap: $gutter-sm $gutter-md;

  @media (min-width: $breakpoint-md) {
    grid-template-columns: auto auto;
  }
}

.visitor-info__tickets {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(15em, 1fr));
  gap: $gutter-sm $gutter-md;

  margin-top: $gutter-sm;

  .form-input {
    display: grid;
    grid-template-columns: 2em 1fr;
    align-items: center;
  }

  .form-input__label {
    margin: 0;
  }

  @media (min-width: $breakpoint-md) {
    margin-top: $gutter-md;
  }
}
</style>
